import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, FileUp, Link2, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n, localeSort, seasonOptions } from "@/i18n";
import useTableState from "@/hooks/useTableState";
import { TableToolbar, Th, filterSelectCls, matchSearch, sortRows } from "@/components/DataTable";

const inputCls = "w-full sm:w-auto rounded-md border border-[#1F293D] bg-[#0B0E14] px-2.5 py-2 text-xs text-slate-200 focus:border-[#00FF87] focus:outline-none";

const STATUS_CLS = {
  discovered: "border-sky-500/50 bg-sky-500/10 text-sky-400",
  importing: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  imported: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
  failed: "border-red-500/50 bg-red-500/10 text-red-400",
};
const normStatus = (s) => ({ extracting: "importing", invalid: "failed" }[s] || s);
const SET_STATUSES = ["discovered", "importing", "imported", "failed"];

/** ONE table, one row per checklist set (discovered | importing | imported | failed).
 * Sorting, search and filters persist in the URL. */
export default function ChecklistSetsPanel() {
  const { t, lang } = useI18n();
  const ts = useTableState("s", { sort: "created_at", dir: "desc" });
  const manufacturers = localeSort(["Topps", "Panini", "Futera", "Other"], lang);
  const [editForm, setEditForm] = useState({});
  const [sets, setSets] = useState([]);
  const [sources, setSources] = useState([]);
  const [srcForm, setSrcForm] = useState({ url: "", publisher: "Topps" });
  const [pdfMeta, setPdfMeta] = useState({ manufacturer: "Topps", set_name: "", season: "" });
  const [beckettUrls, setBeckettUrls] = useState({});
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const prevStatus = useRef({});
  const pdfRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, src] = await Promise.all([api.get("/admin/cards/sets"), api.get("/admin/cards/sources")]);
      setSets(s.data.sets);
      setSources(src.data.sources);
    } catch { /* handled by page-level auth guard */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sets.some((s) => normStatus(s.status) === "importing")) return undefined;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [sets, load]);

  const report = (r) => {
    if (r.data.queued) {
      toast.success(t("cards.queued"));
      load();
      return;
    }
    if (r.data.status === "failed") {
      toast.error(`${t("cards.validation_ko")}: ${(r.data.validation?.errors || []).join(", ") || r.data.error || "?"}`);
    } else {
      toast.success(t("cards.imported", { n: r.data.imported, m: r.data.auto_matched }));
      if (r.data.warnings?.length || r.data.blocked) {
        toast.warning(t("cards.import_warnings", { n: r.data.blocked || 0 }));
      }
    }
  };

  const reimport = async (set) => {
    setBusy(set.id);
    try {
      await api.post(`/admin/cards/sets/${set.id}/reimport`);
      toast.success(t("cards.reimport_queued"));
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const removeSet = async (set) => {
    if (!window.confirm(t("cards.delete_set_confirm"))) return;
    const purge = window.confirm(t("cards.delete_set_purge_confirm"));
    try {
      await api.delete(`/admin/cards/sets/${set.id}?purge_cards=${purge}`);
      toast.success(t("cards.delete_set"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const verify = async (set) => {
    const url = (beckettUrls[set.id] || "").trim();
    if (!url) return;
    setBusy(`v-${set.id}`);
    try {
      const r = await api.post(`/admin/cards/sets/${set.id}/verify`, { url });
      const v = r.data.verification;
      toast.success(`Beckett: ${v.agreed}/${v.compared} ✓ · ${v.disagreed} ${t("cards.disagreements")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const saveSet = async (set) => {
    const f = editForm[set.id] || {};
    const body = {};
    if (f.set_name !== undefined && f.set_name !== (set.set_name || "")) body.set_name = f.set_name;
    if (f.season !== undefined && f.season !== (set.season || "")) body.season = f.season;
    if (f.source_url !== undefined && f.source_url !== (set.source_url || "")) body.source_url = f.source_url;
    if (f.manufacturer !== undefined && f.manufacturer !== set.publisher) body.manufacturer = f.manufacturer;
    if (!Object.keys(body).length) return;
    setBusy(`e-${set.id}`);
    try {
      await api.patch(`/admin/cards/sets/${set.id}`, body);
      toast.success(t("cards.set_saved"));
      setEditForm({ ...editForm, [set.id]: undefined });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const addSource = async () => {
    if (!srcForm.url.trim()) return;
    try {
      await api.post("/admin/cards/sources", srcForm);
      setSrcForm({ ...srcForm, url: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeSource = async (url) => {
    try {
      await api.post("/admin/cards/sources/delete", { url });
      toast.success(t("cards.source_deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadPdf = async () => {
    const f = pdfRef.current?.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("manufacturer", pdfMeta.manufacturer);
    fd.append("set_name", pdfMeta.set_name);
    fd.append("season", pdfMeta.season);
    setBusy("pdf");
    try {
      report(await api.post("/admin/cards/import-pdf", fd));
      pdfRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const publishers = useMemo(() => localeSort([...new Set(sets.map((s) => s.publisher).filter(Boolean))], lang), [sets, lang]);
  const seasons = useMemo(() => localeSort([...new Set(sets.map((s) => s.season).filter(Boolean))], lang), [sets, lang]);

  const rows = (() => {
    let r = sets.filter((s) => matchSearch(s, ts.get("q"), ["set_name", "source_url", "publisher"]));
    const st = ts.get("status");
    if (st) r = r.filter((s) => normStatus(s.status) === st);
    const pub = ts.get("publisher");
    if (pub) r = r.filter((s) => s.publisher === pub);
    const se = ts.get("season");
    if (se) r = r.filter((s) => s.season === se);
    return sortRows(r, ts.get("sort"), ts.get("dir"));
  })();

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="checklist-sets-panel">
      <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.sets_title")}</h2>
      <p className="mb-4 font-mono2 text-[10px] text-slate-500">{t("cards.rc_rule_hint")}</p>

      <div className="mb-4" data-testid="checklist-sources">
        <p className="mb-2 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.sources_title")}</p>
        <div className="mb-2 flex flex-wrap gap-2">
          {sources.map((s) => (
            <span key={s.url} className="flex items-center gap-1.5 rounded-full border border-[#1E2638] px-2.5 py-1 font-mono2 text-[10px] text-slate-400" data-testid="source-chip">
              <Link2 className="h-3 w-3 text-[#00F0FF]" />
              <span className="max-w-56 truncate">{s.url}</span>
              <span className="text-[#00FF87]">{s.publisher}</span>
              <button data-testid="source-delete-button" onClick={() => removeSource(s.url)} className="text-slate-500 hover:text-red-400">
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="source-url-input"
            className={`${inputCls} sm:flex-1 sm:min-w-64`}
            placeholder="https://… (page checklist ou PDF)"
            value={srcForm.url}
            onChange={(e) => setSrcForm({ ...srcForm, url: e.target.value })}
          />
          <select data-testid="source-publisher-select" className={inputCls} value={srcForm.publisher} onChange={(e) => setSrcForm({ ...srcForm, publisher: e.target.value })}>
            {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button data-testid="source-add-button" onClick={addSource} className="rounded-md border border-[#1F293D] px-3 py-2 font-mono2 text-[11px] uppercase tracking-wider text-slate-300 hover:border-[#00FF87]/50 hover:text-[#00FF87]">
            {t("cards.add_source")}
          </button>
        </div>
        <p className="mt-1.5 font-mono2 text-[10px] text-slate-600">{t("cards.source_hint")}</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 border-t border-[#1E2638] pt-4" data-testid="pdf-upload-row">
        <input ref={pdfRef} data-testid="import-pdf-input" type="file" accept=".pdf" className="text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-[#1F293D] file:px-3 file:py-2 file:font-mono2 file:text-[11px] file:uppercase file:text-slate-200" />
        <select className={inputCls} value={pdfMeta.manufacturer} onChange={(e) => setPdfMeta({ ...pdfMeta, manufacturer: e.target.value })} data-testid="import-pdf-manufacturer-select">
          {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className={`${inputCls} sm:w-44`} placeholder={t("cards.set_name")} value={pdfMeta.set_name} onChange={(e) => setPdfMeta({ ...pdfMeta, set_name: e.target.value })} data-testid="import-pdf-set-input" />
        <select className={`${inputCls} sm:w-28`} value={pdfMeta.season} onChange={(e) => setPdfMeta({ ...pdfMeta, season: e.target.value })} data-testid="import-pdf-season-select">
          <option value="">{t("cards.season_auto")}</option>
          {seasonOptions().map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="import-pdf-button" onClick={uploadPdf} disabled={busy === "pdf"} className="btn-volt disabled:opacity-50">
          <FileUp className="h-3.5 w-3.5" /> {busy === "pdf" ? t("common.loading") : "PDF"}
        </button>
        <p className="w-full font-mono2 text-[10px] text-slate-600">{t("cards.pdf_hint")}</p>
      </div>

      <div data-testid="checklist-sets-table">
        <TableToolbar ts={ts} t={t}>
          <select data-testid="s-filter-status" className={filterSelectCls} value={ts.get("status")} onChange={(e) => ts.set("status", e.target.value)}>
            <option value="">{t("cards.col_status")} — {t("filters.all")}</option>
            {SET_STATUSES.map((s) => <option key={s} value={s}>{t(`cards.set_${s}`)}</option>)}
          </select>
          <select data-testid="s-filter-publisher" className={filterSelectCls} value={ts.get("publisher")} onChange={(e) => ts.set("publisher", e.target.value)}>
            <option value="">{t("cards.manufacturer")} — {t("filters.all")}</option>
            {publishers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="s-filter-season" className={filterSelectCls} value={ts.get("season")} onChange={(e) => ts.set("season", e.target.value)}>
            <option value="">{t("cards.season")} — {t("filters.all")}</option>
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </TableToolbar>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="w-6 py-2 pr-1" />
                <Th k="publisher" label={t("cards.manufacturer")} ts={ts} />
                <Th k="set_name" label={t("cards.col_set")} ts={ts} />
                <Th k="season" label={t("cards.season")} ts={ts} />
                <th className="py-2 pr-3">{t("cards.col_source")}</th>
                <Th k="status" label={t("cards.col_status")} ts={ts} />
                <Th k="rows_imported" label={t("cards.col_rows")} ts={ts} />
                <Th k="created_at" label={t("cards.col_date")} ts={ts} />
                <th className="py-2">{t("admin.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const st = normStatus(s.status);
                const isOpen = expanded === s.id;
                return (
                  <Fragment key={s.id}>
                    <tr className="border-b border-slate-800/60 text-slate-300" data-testid={`set-row-${s.id}`}>
                      <td className="py-2 pr-1">
                        <button data-testid={`set-expand-${s.id}`} onClick={() => setExpanded(isOpen ? null : s.id)} className="text-slate-500 transition-colors hover:text-slate-200">
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      <td className="py-2 pr-3 font-mono2">{s.publisher}</td>
                      <td className="max-w-56 truncate py-2 pr-3 font-mono2 font-bold text-slate-200" title={s.set_name}>{s.set_name || "—"}</td>
                      <td className="whitespace-nowrap py-2 pr-3 font-mono2">{s.season || "—"}</td>
                      <td className="max-w-44 truncate py-2 pr-3">
                        {s.source_url ? (
                          <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="font-mono2 text-[10px] text-[#00F0FF] hover:underline" data-testid={`set-source-${s.id}`} title={s.source_url}>{s.source_url}</a>
                        ) : (
                          <span className="font-mono2 text-[10px] uppercase text-slate-600">{s.kind || "—"}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`rounded-full border px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[st] || ""}`} data-testid="set-status-badge">
                          {t(`cards.set_${st}`)}
                        </span>
                        {s.error && (
                          <span className="mt-1 block max-w-56 truncate font-mono2 text-[10px] text-red-400" title={s.error} data-testid={`set-row-error-${s.id}`}>
                            {s.error}
                          </span>
                        )}
                        {!s.error && st === "discovered" && (s.rows_imported ?? 0) === 0 && (
                          <span className="mt-1 block font-mono2 text-[10px] text-amber-400" data-testid={`set-row-empty-${s.id}`}>
                            {t("cards.set_empty_hint")}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono2">{s.rows_imported ?? 0}</td>
                      <td className="whitespace-nowrap py-2 pr-3 font-mono2 text-[10px] text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`set-reimport-${s.id}`}
                            onClick={() => reimport(s)}
                            disabled={busy === s.id || st === "importing"}
                            title={t("cards.reimport")}
                            className="rounded-md border border-[#1F293D] p-1.5 text-slate-300 transition-colors hover:border-[#00FF87]/50 hover:text-[#00FF87] disabled:opacity-40"
                          >
                            <RefreshCw className={`h-3 w-3 ${busy === s.id ? "animate-spin" : ""}`} />
                          </button>
                          <button
                            data-testid={`set-delete-${s.id}`}
                            onClick={() => removeSet(s)}
                            disabled={st === "importing"}
                            title={t("cards.col_delete")}
                            className="rounded-md border border-[#1F293D] p-1.5 text-slate-500 transition-colors hover:border-red-500/50 hover:text-red-400 disabled:opacity-40"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-slate-800/60" data-testid={`set-details-${s.id}`}>
                        <td colSpan={9} className="bg-[#07090E]/60 px-4 py-3">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono2 text-[10px] text-slate-500">
                            {s.extraction_method && <span className="uppercase text-[#00F0FF]">{t("cards.method")}: {s.extraction_method}</span>}
                            {s.validation && (
                              <span className={`uppercase ${s.validation.passed ? "text-emerald-400" : "text-red-400"}`} title={(s.validation.errors || []).join("\n")} data-testid="set-validation-badge">
                                {s.validation.passed ? t("cards.validation_ok") : t("cards.validation_ko")}
                              </span>
                            )}
                            <span>{s.auto_accepted ?? 0} {t("cards.auto_accepted")} · {s.pending_review ?? 0} {t("cards.pending_review_count")}</span>
                            {s.disagreements > 0 && <span className="text-amber-400">{s.disagreements} {t("cards.disagreements")}</span>}
                            {s.error && <span className="text-red-400" data-testid="set-error">{s.error}</span>}
                          </div>
                          {(s.validation?.warnings?.length > 0 || s.rows_blocked > 0) && (
                            <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 font-mono2 text-[10px] uppercase tracking-wider text-amber-300" data-testid={`set-warnings-${s.id}`}>
                              {t("cards.import_warnings", { n: s.rows_blocked || 0 })}
                              {s.validation?.warnings?.length > 0 && <span className="ml-2 normal-case text-amber-200/80">{s.validation.warnings.join(" · ")}</span>}
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <input
                              className={`${inputCls} sm:w-64`}
                              placeholder={t("cards.beckett_placeholder")}
                              value={beckettUrls[s.id] ?? s.beckett_url ?? ""}
                              onChange={(e) => setBeckettUrls({ ...beckettUrls, [s.id]: e.target.value })}
                              data-testid={`set-beckett-input-${s.id}`}
                            />
                            <button
                              data-testid={`set-verify-${s.id}`}
                              onClick={() => verify(s)}
                              disabled={busy === `v-${s.id}`}
                              className="flex items-center gap-1 rounded-md border border-[#1F293D] px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 hover:border-[#FFD600]/50 hover:text-[#FFD600] disabled:opacity-40"
                            >
                              <ShieldCheck className="h-3 w-3" /> {t("cards.verify")}
                            </button>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#1E2638]/60 pt-3" data-testid={`set-edit-${s.id}`}>
                            <input
                              className={`${inputCls} sm:w-56`}
                              value={editForm[s.id]?.set_name ?? s.set_name ?? ""}
                              placeholder={t("cards.set_name")}
                              onChange={(e) => setEditForm({ ...editForm, [s.id]: { ...editForm[s.id], set_name: e.target.value } })}
                              data-testid={`set-edit-name-${s.id}`}
                            />
                            <select
                              className={inputCls}
                              value={editForm[s.id]?.season ?? s.season ?? ""}
                              onChange={(e) => setEditForm({ ...editForm, [s.id]: { ...editForm[s.id], season: e.target.value } })}
                              data-testid={`set-edit-season-${s.id}`}
                            >
                              <option value="">—</option>
                              {localeSort([...new Set([...(s.season ? [s.season] : []), ...seasonOptions()])], lang).map((se) => <option key={se} value={se}>{se}</option>)}
                            </select>
                            <select
                              className={inputCls}
                              value={editForm[s.id]?.manufacturer ?? s.publisher ?? "Topps"}
                              onChange={(e) => setEditForm({ ...editForm, [s.id]: { ...editForm[s.id], manufacturer: e.target.value } })}
                              data-testid={`set-edit-manufacturer-${s.id}`}
                            >
                              {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <input
                              className={`${inputCls} sm:min-w-64 sm:flex-1`}
                              value={editForm[s.id]?.source_url ?? s.source_url ?? ""}
                              placeholder="https://…"
                              onChange={(e) => setEditForm({ ...editForm, [s.id]: { ...editForm[s.id], source_url: e.target.value } })}
                              data-testid={`set-edit-url-${s.id}`}
                            />
                            <button
                              data-testid={`set-edit-save-${s.id}`}
                              onClick={() => saveSet(s)}
                              disabled={busy === `e-${s.id}`}
                              className="btn-volt !px-3 !py-1.5 disabled:opacity-40"
                            >
                              {t("cards.save")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500">—</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
